import { useState } from "react";
import { heroStats, heroQuickTags } from "@/mocks/homeData";

interface HeroProps {
  query: string;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
}

export default function Hero({ query, onQueryChange, onSearch }: HeroProps) {
  const [focused, setFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch();
  };

  return (
    <section id="top" className="relative w-full min-h-[560px] md:min-h-[720px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20abstract%20collage%20of%20movie%20theatre%20light%20beams%20and%20floating%20book%20pages%20in%20warm%20amber%20and%20deep%20teal%20grading%2C%20dreamlike%20atmosphere%2C%20soft%20bokeh%2C%20artistic%20textured%20backdrop%2C%20no%20text&width=1920&height=1080&seq=hero-portal-bg-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/60"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 py-24 md:py-28">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-xs md:text-sm backdrop-blur animate-fade-in">
            <i className="ri-sparkling-2-line text-primary-300" aria-hidden="true"></i>
            影视 · 小说 · 漫画 · 听书 一站式在线
          </span>

          <h1 className="mt-5 font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight text-balance animate-fade-up">
            中文在线字幕免费高清
            <span className="block mt-2 text-primary-300">高清影视与免费阅读的聚合站</span>
          </h1>

          <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-background-100/90 animate-fade-up">
            这里聚合了免费小说、高清影视、漫画与听书资源，中文字幕同步更新，支持无广告版在线观看。
            无论是通勤路上、睡前时光还是午后小憩，都能一站式满足阅读、观影与听书需求。
          </p>

          <form onSubmit={handleSubmit} className="mt-8 w-full max-w-xl animate-fade-up">
            <div
              className={`flex flex-col sm:flex-row items-stretch gap-2 p-2 rounded-xl bg-background-50/95 backdrop-blur border ${
                focused ? "border-primary-400" : "border-background-200"
              } transition-colors`}
            >
              <div className="flex items-center flex-1 gap-2 px-3">
                <i className="ri-search-line text-foreground-500 text-base" aria-hidden="true"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  placeholder="搜索影视、小说、漫画或听书，如「悬疑」「玄幻」"
                  aria-label="站内内容检索"
                  className="w-full bg-transparent py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                立即检索
              </button>
            </div>
          </form>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs text-background-100/70">热门：</span>
            {heroQuickTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onQueryChange(tag)}
                className="px-3 py-1.5 rounded-full text-xs text-background-100 bg-background-50/10 border border-background-50/20 hover:bg-background-50/20 transition-colors cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pt-6 border-t border-background-50/15">
            {heroStats.map((s) => (
              <div key={s.label}>
                <div className="font-heading text-xl md:text-2xl font-bold text-primary-300">{s.value}</div>
                <div className="mt-1 text-xs text-background-100/80">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}